import { useEffect, useState } from 'react';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import useGetResponseMarketing from '../../../hooks/useGetResponseMarketing';

// The campaigns endpoint may return a bare array or { campaigns: [...] }.
const normalizeCampaignList = data => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.campaigns)) return data.campaigns;
  return [];
};

// Read a campaign's identifier regardless of the casing the API used.
const campaignId = campaign =>
  campaign ? String(campaign.campaignId || campaign.campaignID || campaign.id || '') : '';

// Settings for the "GetResponse-Sync" block: ensures the contact exists in the
// GetResponse list and pushes the voucher of a referenced issueVoucher block
// into custom fields (<prefix>_voucher_code/_pin/_value/_expiry,
// <prefix>_activation_url, <prefix>_cycle_id).
const GetResponseSyncSettings = ({ block, blocks = [], readOnly, onConfigChange }) => {
  const { getCampaigns } = useGetResponseMarketing();
  const config = block.config || {};
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const result = await getCampaigns();
        if (active && result !== null) setCampaigns(normalizeCampaignList(result));
      } catch {
        // The campaign options are optional; the default main list always works.
      }
    };
    load();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const voucherBlocks = blocks.filter(
    candidate => candidate.type === 'issueVoucher' && !candidate.config?.perEntertainment
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        select
        size='small'
        label='GetResponse-Liste'
        value={config.campaignId || ''}
        disabled={readOnly}
        onChange={event => onConfigChange({ campaignId: event.target.value || null })}
      >
        <MenuItem value=''>Standard-Liste (Umgebung)</MenuItem>
        {campaigns.map(campaign => (
          <MenuItem key={campaignId(campaign)} value={campaignId(campaign)}>
            {campaign.name || campaignId(campaign)}
          </MenuItem>
        ))}
      </TextField>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <TextField
          select
          size='small'
          label='Gutschein aus Block'
          value={config.voucherBlockId || ''}
          disabled={readOnly}
          onChange={event => onConfigChange({ voucherBlockId: event.target.value || null })}
        >
          <MenuItem value=''>Kein Gutschein (nur Kontakt anlegen)</MenuItem>
          {voucherBlocks.map(candidate => (
            <MenuItem key={candidate.blockId} value={candidate.blockId}>
              Gutschein erstellen ({Number(candidate.config?.amount) || 0} €) — {candidate.blockId}
            </MenuItem>
          ))}
        </TextField>
        {voucherBlocks.length === 0 && (
          <Typography variant='caption' color='text.secondary'>
            Kein passender „Gutschein erstellen“-Block im Flow (Blöcke „je Entertainment“ werden
            nicht unterstützt).
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <TextField
          key={`${block.blockId}-prefix`}
          size='small'
          label='Feld-Präfix'
          defaultValue={config.fieldPrefix || 'birthday'}
          disabled={readOnly}
          onChange={event =>
            onConfigChange({ fieldPrefix: event.target.value.trim().toLowerCase() })
          }
        />
        <Typography variant='caption' color='text.secondary'>
          Custom Fields: {config.fieldPrefix || 'birthday'}_voucher_code / _pin / _value / _expiry,
          _activation_url, _cycle_id — fehlende Felder werden automatisch als versteckte Textfelder
          angelegt.
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <TextField
          key={`${block.blockId}-linkbase`}
          size='small'
          label='Buchungs-Link-Basis (Aktivierungslink)'
          placeholder='https://fun-zone.de/buchen'
          defaultValue={config.linkBaseUrl || ''}
          disabled={readOnly}
          onChange={event => onConfigChange({ linkBaseUrl: event.target.value.trim() })}
        />
        <Typography variant='caption' color='text.secondary'>
          Daraus entsteht der Aktivierungslink mit fzVoucher + fzVoucherPin — das Booking-Widget
          übernimmt den Gutschein damit automatisch im Checkout.
        </Typography>
      </Box>
      <Typography variant='caption' color='text.secondary'>
        Der Block wartet, bis die Felder verifiziert in GetResponse stehen. Der Trigger-Tag für die
        GetResponse-Automation gehört in einen eigenen „GetResponse-Tag“-Block direkt danach — so
        ist garantiert, dass der Tag erst nach erfolgreichem Sync gesetzt wird.
      </Typography>
    </Box>
  );
};

export default GetResponseSyncSettings;
